
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  assessmentCreate,
  assessmentGet,
} from "utils/assessmentApplication/assessmentService";


export interface AssessmentSession {
  sessionId: string;
  participantId: string;
  assessmentId: string;
  learnerName: string;
  gameId?: string;
  learnerId?: string;
  quest?: string;
  hasCompletedBefore: boolean;
}

export const useAssessmentSession = (formData?: any, learnerPlayList?: any, currentQuestNo?: any) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState<AssessmentSession | null>(null);
  const [loading, setLoading] = useState(false);

  // Load session from URL params or formData
  const loadSession = async () => {
    const assessmentId = searchParams.get("assessmentId");
    const gameId = learnerPlayList?.GameId || searchParams.get("gameId");
    const learnerId = learnerPlayList?.playerId || searchParams.get("learnerId");
    const quest = currentQuestNo || learnerPlayList?.questId || searchParams.get("quest") || 'default';
    const learnerName = learnerPlayList?.LearnerProfile?.nickName ||
      learnerPlayList?.LearnerProfile?.name ||
      'Learner';
    const questKey = `assessmentId:quest:${String(quest)}`;
    const lockKey = `${questKey}:lock`;

    setLoading(true);

    try {
      if (assessmentId) {
        const assessment = await assessmentGet(assessmentId);
        if (assessment?.updatedData) {
          const data = assessment.updatedData as any;
          const sessionData: AssessmentSession = {
            sessionId: String(data.assessmentId),
            participantId: String(data.assessmentParticipantId),
            assessmentId: String(data.assessmentId),
            learnerName,
            gameId: data.assessmentGameId ? String(data.assessmentGameId) : undefined,
            learnerId: learnerId ? String(learnerId) : undefined,
            quest: String(data.assessmentQuestNo || quest),
            hasCompletedBefore: data.assessmentEvaluationJson !== null,
          };

          setSession(sessionData);
          setLoading(false);
          return sessionData;
        }
      }

      if (!gameId || !learnerId) {
        navigate("/");
        return null;
      }

      const cachedAssessmentId = localStorage.getItem(questKey);
      if (cachedAssessmentId) {
        const assessment = await assessmentGet(cachedAssessmentId);
        if (assessment?.updatedData) {
          const data = assessment.updatedData as any;
          const sessionData: AssessmentSession = {
            sessionId: String(data.assessmentId),
            participantId: String(data.assessmentParticipantId),
            assessmentId: String(data.assessmentId),
            learnerName,
            gameId: String(data.assessmentGameId || gameId),
            learnerId: String(learnerId),
            quest: String(data.assessmentQuestNo || quest),
            hasCompletedBefore: data.assessmentEvaluationJson !== null,
          };

          setSession(sessionData);
          setLoading(false);
          return sessionData;
        }
      }

      if (localStorage.getItem(lockKey) === "1") {
        for (let i = 0; i < 20; i += 1) {
          await new Promise((resolve) => setTimeout(resolve, 150));
          const retryId = localStorage.getItem(questKey);
          if (retryId) {
            const assessment = await assessmentGet(retryId);
            if (assessment?.updatedData) {
              const data = assessment.updatedData as any;
              const sessionData: AssessmentSession = {
                sessionId: String(data.assessmentId),
                participantId: String(data.assessmentParticipantId),
                assessmentId: String(data.assessmentId),
                learnerName,
                gameId: String(data.assessmentGameId || gameId),
                learnerId: String(learnerId),
                quest: String(data.assessmentQuestNo || quest),
                hasCompletedBefore: data.assessmentEvaluationJson !== null,
              };

              setSession(sessionData);
              setLoading(false);
              return sessionData;
            }
          }
        }
      }

      localStorage.setItem(lockKey, "1");

      const scenarioText =
        formData?.scenario
        ?? formData?.blockText
        ?? formData?.contentByLanguage
        ?? formData?.speechContent
        ?? formData?.content
        ?? learnerPlayList?.scenario
        ?? learnerPlayList?.blockText
        ?? learnerPlayList?.contentByLanguage
        ?? learnerPlayList?.speechContent
        ?? learnerPlayList?.content
        ?? "default";

      const assessmentResponse = await assessmentCreate({
        participantId: String(learnerId),
        gameId: String(gameId),
        questNo: String(quest),
        scenario: scenarioText,
      });

      const createdAssessmentId = assessmentResponse?.data ?? assessmentResponse?.updatedData?.assessmentId;
      if (!createdAssessmentId) {
        throw new Error("Failed to create or load assessment session");
      }

      const createdAssessment = await assessmentGet(createdAssessmentId);
      const createdData = createdAssessment?.updatedData as any;
      if (!createdData) {
        throw new Error("Assessment created but could not be loaded");
      }

      const sessionData: AssessmentSession = {
        sessionId: String(createdData.assessmentId),
        participantId: String(createdData.assessmentParticipantId),
        assessmentId: String(createdData.assessmentId),
        learnerName,
        gameId: String(createdData.assessmentGameId || gameId),
        learnerId: String(learnerId),
        quest: String(createdData.assessmentQuestNo || quest),
        hasCompletedBefore: createdData.assessmentEvaluationJson !== null,
      };

      localStorage.setItem(questKey, String(createdData.assessmentId));
      setSession(sessionData);
      setLoading(false);
      return sessionData;
    } catch (error) {
      console.error('Error creating/loading session:', error);
      setLoading(false);
      return null;
    } finally {
      localStorage.removeItem(lockKey);
    }
  };



  return {
    user: { id: 'user', email: 'user@example.com' },
    checkingAuth: false,
    session,
    loading,
    loadSession,
    handleLogout: () => { }
  };
};
